import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import {
  formatCountdown,
  parseEventTime,
} from "../../../../util/formatDate.js";
import Expandable from "../Expandable.jsx";
import EventRow from "./EventRow.jsx";
import s from "./Events.module.css";
import EventsHeader from "./EventsHeader.jsx";
import EventTabs, { LIVE_TAB_ID, OFF_TAB_ID, PANEL_ID } from "./EventTabs.jsx";

function sortByDate(events) {
  return [...events].sort(
    (a, b) => (parseEventTime(a) || 0) - (parseEventTime(b) || 0),
  );
}

function filterUpcoming(events) {
  const now = Date.now();
  return events
    .map((ev) => ({ ev, date: parseEventTime(ev) }))
    .filter(({ date }) => date && date.getTime() >= now)
    .sort((a, b) => a.date - b.date)
    .map(({ ev }) => ev);
}

function computeNext(liveSorted, offSorted) {
  const hasLive = liveSorted.length > 0;
  const hasOff = offSorted.length > 0;
  // Always prefer the next live event. Only fall back to non-live if
  // there are no upcoming live events.
  const nextIsLive = hasLive;
  return {
    nextIsLive,
    nextEvent: nextIsLive ? liveSorted[0] : offSorted[0] || null,
    nextDate: nextIsLive
      ? parseEventTime(liveSorted[0])
      : hasOff
      ? parseEventTime(offSorted[0])
      : null,
  };
}

function useCountdown(active, date) {
  const [countdown, setCountdown] = useState("");
  useEffect(() => {
    if (!active || !date) {
      setCountdown("");
      return undefined;
    }
    const update = () => setCountdown(formatCountdown(date));
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, [active, date]);
  return countdown;
}

export default function Events({ liveEvents, nonLiveEvents }) {
  const liveSorted = useMemo(() => sortByDate(liveEvents || []), [liveEvents]);
  const offSorted = useMemo(
    () => filterUpcoming(nonLiveEvents || []),
    [nonLiveEvents],
  );

  const hasLive = liveSorted.length > 0;
  const hasOff = offSorted.length > 0;
  const hasBoth = hasLive && hasOff;

  const { nextIsLive, nextEvent, nextDate } = useMemo(
    () => computeNext(liveSorted, offSorted),
    [liveSorted, offSorted],
  );

  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState(nextIsLive || !hasOff ? "live" : "offcamera");

  // Keep tab valid if data changes.
  useEffect(() => {
    if (tab === "live" && !hasLive) setTab("offcamera");
    else if (tab === "offcamera" && !hasOff) setTab("live");
  }, [tab, hasLive, hasOff]);

  const countdown = useCountdown(nextIsLive, nextDate);

  if (!nextEvent) return null;

  const bodyId = "events-body";
  const activeList = tab === "live" ? liveSorted : offSorted;
  const activeLabelId = tab === "live" ? LIVE_TAB_ID : OFF_TAB_ID;

  return (
    <section className={s.container} aria-label="Upcoming events">
      <EventsHeader
        nextEvent={nextEvent}
        nextIsLive={nextIsLive}
        nextDate={nextDate}
        countdown={countdown}
        totalCount={liveSorted.length + offSorted.length}
        expanded={expanded}
        bodyId={bodyId}
        onToggle={() => setExpanded((v) => !v)}
      />

      <Expandable open={expanded} id={bodyId}>
        {hasBoth && (
          <EventTabs
            tab={tab}
            setTab={setTab}
            liveCount={liveSorted.length}
            offCount={offSorted.length}
          />
        )}

        <ul
          className={s.list}
          id={PANEL_ID}
          role={hasBoth ? "tabpanel" : undefined}
          aria-labelledby={hasBoth ? activeLabelId : undefined}
        >
          {activeList.map((ev) => (
            <EventRow key={ev.influencer_event_id} event={ev} variant={tab} />
          ))}
        </ul>
      </Expandable>
    </section>
  );
}

Events.propTypes = {
  liveEvents: PropTypes.array,
  nonLiveEvents: PropTypes.array,
};
